<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Edge accent -->
      <div class="left-edge"></div>

      <!-- Nav -->
      <nav class="ob-nav">
        <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="ob-logo" />
        <div class="ob-nav-right">
          <span class="ob-nav-pair">{{ loName }} × {{ realtorName }}</span>
   
        </div>
      </nav>

      <!-- Full-bleed hero -->
      <section class="ob-hero">
        <div class="oh-inner">
          <div class="oh-number">01</div>
          <div class="oh-content">
            <div class="oh-eyebrow">Mortgage Partnership</div>
            <h1 class="oh-h1" v-html="heroH1Html"></h1>
            <p class="oh-body">{{ heroBody }}</p>
            <div class="oh-btns">
              <a class="oh-btn" :href="applyRightLink">{{ heroPrimaryText }}</a>
              <a v-if="applyLeftLink" class="oh-btn-ghost" :href="applyLeftLink">{{ heroSecondaryText }}</a>
            </div>
          </div>
        </div>
        <!-- Bottom cut -->
        <div class="oh-cut"></div>
      </section>

      <!-- Agents section -->
      <section class="ob-agents">
        <div class="oa-inner">

          <!-- LO card -->
          <div class="ob-card lo-ob">
            <div class="obc-number">—01</div>
            <div class="obc-header lo-header">
              <span class="obc-label">Loan Officer</span>
            </div>
            <div class="obc-profile">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="obc-img" />
              <div>
                <div class="obc-name">{{ loName }}</div>
                <div class="obc-role">{{ loTitle }}</div>
                <div v-if="loinfo && loinfo.nmls" class="obc-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="obc-line lo-line"></div>
            <div class="obc-contacts">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="obc-link lo-ol">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="obc-link lo-ol">{{ loinfo.email }}</a>
            </div>
          
          </div>

          <!-- Divider -->
          <div class="oa-gap">
            <div class="oa-vline"></div>
          </div>

          <!-- RE card -->
          <div class="ob-card re-ob">
            <div class="obc-number re-num">—02</div>
            <div class="obc-header re-header">
              <span class="obc-label re-lbl">{{ realtor?.partner_title || 'Partner' }}</span>
            </div>
            <div class="obc-profile">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="obc-img" />
              <div>
                <div class="obc-name">{{ realtorName }}</div>
                <div class="obc-role">{{ realtor && realtor.brokerage_name }}</div>
                <div v-if="realtor && realtor.nmls" class="obc-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="obc-line re-line"></div>
            <div class="obc-contacts">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="obc-link re-ol">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="obc-link re-ol">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="obc-link re-ol" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>

        </div>
      </section>

      <!-- Footer -->
      <div class="ob-footer">
        <span>{{ loName }}</span>
        <span class="of-sep">·</span>
        <span>{{ loTitle }}</span>
        <div class="of-flex"></div>
        <div class="of-accent-block"></div>
      </div>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandObsidian",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Bold moves,<br/><em>clear path.</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700;800&family=Fraunces:ital,opsz,wght@0,9..144,700;1,9..144,300&display=swap');

.page {
  min-height: 100vh;
  background: #13141a;
  color: #e4e6f0;
  font-family: 'Urbanist', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --bg: #13141a;
  --card-bg: #1c1e26;
  --border: rgba(228,230,240,0.08);
  --muted: #7880a0;
  position: relative;
}
.root { display: flex; flex-direction: column; min-height: 100vh; position: relative; }

/* LEFT EDGE */
.left-edge { position: fixed; left: 0; top: 0; bottom: 0; width: 4px; background: linear-gradient(180deg, var(--p) 0%, var(--a) 100%); z-index: 100; }

/* NAV */
.ob-nav { display: flex; align-items: center; justify-content: space-between; padding: 20px 60px 20px 72px; border-bottom: 1px solid var(--border); }
.ob-logo { width: 140px; max-height:72px; object-fit: contain; filter: brightness(0) invert(1); opacity: 0.75; }
.ob-nav-right { display: flex; align-items: center; gap: 24px; }
.ob-nav-pair { font-size: 12px; color: var(--muted); }
.ob-cta { font-size: 12px; font-weight: 600; text-decoration: none; background: var(--a); color: #13141a; padding: 9px 20px; border-radius: 4px; transition: opacity 0.2s; }
.ob-cta:hover { opacity: 0.88; }

/* HERO */
.ob-hero { background: var(--p); position: relative; overflow: hidden; }
.oh-inner { display: flex; max-width: 1200px; margin: 0 auto; padding: 72px 60px 72px 72px; gap: 60px; align-items: center; }
.oh-number { font-family: 'Fraunces', serif; font-size: 120px; font-weight: 700; color: rgba(255,255,255,0.04); line-height: 1; flex-shrink: 0; user-select: none; }
.oh-content { max-width: 600px; }
.oh-eyebrow { font-size: 10px; font-weight: 500; letter-spacing: 0.24em; text-transform: uppercase; color: var(--a); margin-bottom: 18px; opacity: 0.8; }
.oh-h1 { font-family: 'Fraunces', serif; font-size: clamp(38px, 5vw, 66px); font-weight: 700; line-height: 1.08; color: #f0f2f8; margin: 0 0 20px; }
.oh-h1 em { font-style: italic; color: var(--a); font-weight: 300; }
.oh-body { font-size: 15px; line-height: 1.78; color: rgba(228,230,240,0.5); margin-bottom: 32px; font-weight: 300; }
.oh-btns { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;}
.oh-btn { font-size: 13px; font-weight: 600; text-decoration: none; background: var(--a); color: #13141a; padding: 13px 28px; border-radius: 4px; transition: opacity 0.2s; width: 55%; text-align: center; }
.oh-btn:hover { opacity: 0.88; }
.oh-btn-ghost { font-size: 13px; font-weight: 400; text-decoration: none; color: rgba(228,230,240,0.5); border: 1px solid var(--border); padding: 13px 22px; border-radius: 4px; transition: border-color 0.18s; }
.oh-btn-ghost:hover { border-color: rgba(228,230,240,0.25); }
.oh-cut { height: 40px; background: var(--bg); clip-path: polygon(0 100%, 100% 100%, 100% 0); }

/* AGENTS */
.ob-agents { padding: 52px 0; }
.oa-inner { max-width: 900px; margin: 0 auto; padding: 0 60px 0 72px; display: flex; gap: 0; align-items: stretch; }
.ob-card { flex: 1; padding: 32px; position: relative; }
.obc-number { position: absolute; top: 20px; right: 20px; font-family: 'Fraunces', serif; font-size: 36px; font-weight: 700; color: var(--border); line-height: 1; }
.re-num { color: color-mix(in srgb, var(--a) 15%, transparent); }
.obc-header { margin-bottom: 16px; }
.obc-label { font-size: 9px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; color: var(--p); background: color-mix(in srgb, var(--p) 15%, transparent); border: 1px solid color-mix(in srgb, var(--p) 25%, transparent); padding: 4px 10px; border-radius: 3px; }
.re-lbl { color: var(--a); background: color-mix(in srgb, var(--a) 12%, transparent); border-color: color-mix(in srgb, var(--a) 22%, transparent); }
.obc-profile { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 16px; }
.obc-img { width: 56px; border-radius: 6px; object-fit: cover; flex-shrink: 0; border: 1px solid var(--border); }
.obc-name { font-size: 18px; font-weight: 700; color: #e4e6f0; }
.obc-role { font-size: 11px; color: var(--muted); margin-top: 2px; }
.obc-nmls { font-size: 10px; color: color-mix(in srgb, var(--muted) 60%, transparent); margin-top: 2px; }
.obc-line { height: 2px; margin-bottom: 14px; border-radius: 1px; }
.lo-line { background: linear-gradient(90deg, var(--p), transparent); }
.re-line { background: linear-gradient(90deg, var(--a), transparent); }
.obc-contacts { display: flex; flex-direction: column; gap: 5px; margin-bottom: 16px; }
.obc-link { font-size: 11px; text-decoration: none; color: var(--muted); transition: color 0.18s; }
.lo-ol:hover { color: color-mix(in srgb, var(--p) 80%, #aac); }
.re-ol:hover { color: var(--a); }
.obc-cta { display: block; text-align: center; text-decoration: none; font-size: 12px; font-weight: 600; padding: 11px; border-radius: 4px; transition: opacity 0.2s; }
.lo-obc-cta { background: var(--a); color: #13141a; }
.lo-obc-cta:hover { opacity: 0.88; }
.oa-gap { padding: 0 20px; display: flex; align-items: center; }
.oa-vline { width: 1px; height: 100%; background: var(--border); }

/* FOOTER */
.ob-footer { display: flex; align-items: center; padding: 16px 60px 16px 72px; border-top: 1px solid var(--border); }
.ob-footer span { font-size: 11px; color: var(--muted); }
.of-sep { margin: 0 12px; color: var(--border); }
.of-flex { flex: 1; }
.of-accent-block { width: 24px; height: 4px; background: var(--a); border-radius: 2px; }

@media (max-width: 820px) {
  .left-edge { display: none; }
  .ob-nav { padding: 16px 24px; }
  .ob-nav-pair { display: none; }
  .oh-inner { flex-direction: column; padding: 48px 24px; gap: 20px; }
  .oh-number { display: none; }
  .oa-inner { flex-direction: column; padding: 0 24px; }
  .oa-gap { padding: 4px 0; flex-direction: row; }
  .oa-vline { height: 1px; width: 100%; }
  .ob-footer { padding: 14px 24px; }
}
</style>
